import { ApolloClient, createHttpLink, InMemoryCache } from "@apollo/client";
import { setContext } from "@apollo/link-context";
import { onError } from "@apollo/link-error";
import firebase from "firebase/app";
import "firebase/auth";
import {
  GET_CURRENT_PATIENTS,
  GET_PATIENT_BY_MOBILE_NUMBER,
  GET_PRIMARY_PATIENTS_BY_MOBILE_NUMBER,
  UPDATE_PATIENT_EXTERNAL_AUTH_ID,
  VERIFY_EXTERNAL_AUTH_TOKEN,
} from "graphql/profiles";
import sha1 from "js-sha1";

import { GetCurrentPatients } from "graphql/types/GetCurrentPatients";
import { GetPatientByMobileNumber } from "graphql/types/GetPatientByMobileNumber";
import { MedicineCartItem } from "../components/MedicinesCartProvider";
import { Relation } from "graphql/types/globalTypes";
import { getUserNotifyEvents_getUserNotifyEvents_phr_newRecordsCount } from "graphql/types/getUserNotifyEvents";
import {
  fetchTokenFromAuthService,
  signOutUserUsingAuthService,
  fetchNewTokenUsingRefreshToken,
  verifyExternalTokenFromAuthService,
} from "helpers/SSOHelpers";
import Router from "next/router";
import { getUrlParam } from "helpers/commonHelpers";
import _find from "lodash/find";
import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import React, { useEffect, useState, useMemo } from "react";
import { ApolloProvider } from "react-apollo";
import { ApolloProvider as ApolloHooksProvider } from "react-apollo-hooks";
import { localStoragePreserveKeys } from "strings/AppConfig";
import {
  clevertapPushNotification,
  clevertapUserLoginTracking,
  clevertapUserLogoutTracking,
} from "../clevertapTracking";
import { TOKEN_REFETCH_DURATION } from "helpers/constants";
import { dataLayerTracking, gtmTracking, _urTracking } from "../gtmTracking";
import { clientRoutes } from "helpers/clientRoutes";
import { SignedinState } from "components/enums/SignedinState";
import {
  GetPrimaryPatientsByMobileNumber,
  GetPrimaryPatientsByMobileNumber_getPatientByMobileNumber_patient as PrimaryPatient,
} from "graphql/types/GetPrimaryPatientsByMobileNumber";

export enum SSOPlatform {
  MS_TEAMS = "MSTeams",
  PHONEPE_MINIAPP = "PHONEPE_MINIAPP",
  PAYTM_MINIAPP = "PAYTM_MINIAPP",
}

export interface AuthContextProps {
  currentPatientId: string | null;
  setCurrentPatientId: ((pid: string | null) => void) | null;
  getPhrNotifyEvent: getUserNotifyEvents_getUserNotifyEvents_phr_newRecordsCount | null;
  setGetPhrNotifyEvent:
    | ((
        getPhrNotifyEvent: getUserNotifyEvents_getUserNotifyEvents_phr_newRecordsCount | null
      ) => void)
    | null;
  signInError: boolean;
  hasAuthToken: boolean;
  authToken: string;
  signOut: (() => Promise<void>) | null;
  isLoginPopupVisible: boolean;
  setIsLoginPopupVisible: ((isLoginPopupVisible: boolean) => void) | null;
  isConsentFormVisible: boolean;
  setIsConsentFormVisible: ((isConsentFormVisible: boolean) => void) | null;
  isLoading: boolean;
  setIsLoading: ((isLoading: boolean) => void) | null;
  customLoginId: string;
  setCustomLoginId: ((customLoginId: string) => void) | null;
  setAuthToken: ((authToken: string) => void) | null;
  setSSOToken: ((authToken: string) => void) | null;
  isSigningIn: boolean;
  setIsSigningIn: ((isSigningIn: boolean) => void) | null;
  verifyExternalTokenAndLogin: () => void;
  firebaseUser: any;
  setAllCurrentPatients: ((allCurrentPatients: any) => void) | null;
  setCurrentPatient: ((currentPatient: any) => void) | null;
  currentPatient: any;
  allCurrentPatients: any;
  signedin: String;
  setFirebaseUser: ((firebaseUser: any) => void) | null;
  app: firebase.app.App;
  wait: <R, E>(promise: Promise<R>) => [R, E];
  isPartnerLogin: boolean;
  setIsPartnerLogin: ((isPartnerLogin: boolean) => void) | null;
  setSignedin: ((signedin: string) => void) | null;
  isUserCircle: boolean;
  externalAuthData: any;
  setExternalAuthData: ((externalAuthData: any) => void) | null;
  primaryPatients: PrimaryPatient[];
  setPrimaryPatients: (value: PrimaryPatient[]) => void;
  sectionName: string;
  lob: string;
}

const isProduction = process.env.NODE_ENV === "production";
const projectId = process.env.FIREBASE_PROJECT_ID;
let apolloClient: ApolloClient<any>;

const isBrowser = () => typeof window !== "undefined";

export const AuthContext = React.createContext<AuthContextProps>({
  currentPatientId: null,
  setCurrentPatientId: null,
  getPhrNotifyEvent: null,
  setGetPhrNotifyEvent: null,
  signInError: false,
  isSigningIn: true,
  hasAuthToken: false,
  authToken: "",
  signOut: null,
  isConsentFormVisible: false,
  setIsConsentFormVisible: null,
  isLoginPopupVisible: false,
  setIsLoginPopupVisible: null,
  isLoading: false,
  setIsLoading: null,
  customLoginId: "",
  verifyExternalTokenAndLogin: null,
  setIsSigningIn: null,
  setCustomLoginId: null,
  app: null,
  wait: null,
  setAuthToken: null,
  firebaseUser: null,
  currentPatient: null,
  allCurrentPatients: null,
  signedin: null,
  setAllCurrentPatients: null,
  setCurrentPatient: null,
  setFirebaseUser: null,
  setSSOToken: null,
  isPartnerLogin: false,
  setIsPartnerLogin: null,
  setSignedin: null,
  isUserCircle: false,
  externalAuthData: null,
  setExternalAuthData: null,
  primaryPatients: null,
  setPrimaryPatients: null,
  sectionName: null,
  lob: null,
});

export const buildApolloClient = (
  authToken: string,
  handleUnauthenticated: () => void,
  fetchPatients: () => void,
  tokenRefresh: boolean,
  url?: string
) => {
  const errorLink = onError((error) => {
    const { graphQLErrors, operation, forward } = error;
    if (!isProduction) console.error(error);
    if (graphQLErrors) {
      const unauthenticatedError = graphQLErrors.some(
        (gqlError) =>
          gqlError.extensions && gqlError.extensions.code === "UNAUTHENTICATED"
      );
      if (unauthenticatedError) handleUnauthenticated();
    }
    // Retry the request, returning the new observable
    // return forward(operation);
  });

  const authLink = setContext((_, { headers }) => ({
    headers: {
      ...headers,
      Authorization: authToken ? authToken : process.env.NEXT_PUBLIC_AUTH_TOKEN,
    },
  }));

  const httpLink = createHttpLink({
    uri: url ? url : process.env.NEXT_PUBLIC_API_HOST_NAME,
  });
  if (authToken && !tokenRefresh) fetchPatients();
  const link = errorLink.concat(authLink).concat(httpLink);
  const cache = apolloClient ? apolloClient.cache : new InMemoryCache();
  return new ApolloClient({ link, cache });
};

const app = !firebase.apps.length
  ? firebase.initializeApp({
      projectId,
      apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
      appId: "1:537093214409:web:4eec27a7bc6bc1c8",
      authDomain: `${projectId}.firebaseapp.com`,
      databaseURL: `https://${projectId}.firebaseio.com`,
      messagingSenderId: "537093214409",
      storageBucket: "",
    })
  : firebase.app();

// authState(app.auth()).pipe(
//   filter((auth_user) => auth_user !== null),
//   switchMap((auth_user) => {
//     const ref = app.storage().ref(`profile/${auth_user.uid}`);
//     return getDownloadURL(ref);
//   })
// );

function wait<R, E>(promise: Promise<R>): [R, E] {
  return (promise.then(
    (data: R) => [data, null],
    (err: E) => [null, err]
  ) as any) as [R, E];
}

interface ProviderProps {
  sectionName: string;
  lob: string;
}

export const AuthProvider: React.FC<ProviderProps> = (props) => {
  const source = (isBrowser() && getUrlParam("utm_source")) || "";
  const token = isBrowser() && getUrlParam("t");

  const { sectionName, lob } = props;

  const [authToken, setAuthToken] = useState<string>("");
  const [ssoToken, setSSOToken] = useState<string>("");
  const [currentPatientId, _setCurrentPatientId] = useState<
    AuthContextProps["currentPatientId"]
  >(null);
  const setCurrentPatientId = (id: string) => {
    localStorage?.setItem("currentUser", id || "");
    _setCurrentPatientId(id);
  };

  // const [ssoAuthToken, setSsoAuthToken] = useState<string>("");
  // const [isSendingOtp, setIsSendingOtp] = useState<
  //   AuthContextProps["isSendingOtp"]
  // >(false);
  // const [sendOtpError, setSendOtpError] = useState<
  //   AuthContextProps["sendOtpError"]
  // >(false);
  // const [isVerifyingOtp, setIsVerifyingOtp] = useState<
  //   AuthContextProps["isVerifyingOtp"]
  // >(false);
  // const [verifyOtpError, setVerifyOtpError] = useState<
  //   AuthContextProps["verifyOtpError"]
  // >(false);
  // isProHealthSSO what does it do?
  const [isSigningIn, setIsSigningIn] = useState<
    AuthContextProps["isSigningIn"]
  >(true);
  const [signInError, setSignInError] = useState<
    AuthContextProps["signInError"]
  >(false);
  const [isLoginPopupVisible, setIsLoginPopupVisible] = useState<
    AuthContextProps["isLoginPopupVisible"]
  >(false);
  const [isConsentFormVisible, setIsConsentFormVisible] = useState<
    AuthContextProps["isConsentFormVisible"]
  >(false);
  const [isLoading, setIsLoading] = useState<AuthContextProps["isLoading"]>(
    false
  );
  const [customLoginId, setCustomLoginId] = useState<
    AuthContextProps["customLoginId"]
  >("");
  const [getPhrNotifyEvent, setGetPhrNotifyEvent] = useState<
    AuthContextProps["getPhrNotifyEvent"]
  >(null);
  const [isUserLoggedOut, setIsUserLoggedOut] = useState<boolean>(false);
  const [firebaseUser, setFirebaseUser] = useState<
    AuthContextProps["firebaseUser"]
  >(null);
  const [allCurrentPatients, setAllCurrentPatients] = useState<
    AuthContextProps["allCurrentPatients"]
  >(null);
  const [primaryPatients, setPrimaryPatients] = useState<
    AuthContextProps["primaryPatients"]
  >(null);
  const [currentPatient, setCurrentPatient] = useState<
    AuthContextProps["currentPatient"]
  >(null);
  const [signedin, setSignedin] = useState<AuthContextProps["signedin"]>(
    SignedinState.UNRESOLVED
  );
  const [externalAuthData, setExternalAuthData] = useState<
    AuthContextProps["externalAuthData"]
  >(null);
  const [
    gotTokenNowFetchPatients,
    setGotTokenNowFetchPatients,
  ] = useState<boolean>(false);
  const [tokenRefresh, setTokenRefresh] = useState<boolean>(false);
  const [isPartnerLogin, setIsPartnerLogin] = useState<
    AuthContextProps["isPartnerLogin"]
  >(false);
  const [isUserCircle, setIsUserCircle] = useState<boolean>(false);
  const [isUserLoginCompleted, setIsUserLoginCompleted] = useState<boolean>(
    false
  );

  const hasAuthToken = !_isEmpty(authToken);

  const clearData = () => {
    setSSOToken("");
    setAuthToken("");
    setIsUserLoggedOut(true);
    signOutUserUsingAuthService();
    setSignedin(SignedinState.SIGNEDOUT);
    setAllCurrentPatients(null);
    setCurrentPatient(null);
    setCurrentPatientId(null);
    setGotTokenNowFetchPatients(false);
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("mobile_num");
    setPrimaryPatients(null);
  };

  const signOut = () =>
    app
      .auth()
      .signOut()
      .then(() => {
        /*Tracking code end */
        gtmTracking({ category: "Profile", action: "Logout", label: "Logout" });
        dataLayerTracking({ event: "UserLoggedOut" });
        dataLayerTracking({
          event: "logout",
          login_status: "guest",
        });
        clevertapUserLogoutTracking();
        /*Tracking code end */

        /* Iterate over localStorage and remove all keys except
            the keys intended for Clevertap, starting with WZRK_ and email_CT */
        clearData();
        Object.keys(localStorage).forEach((element: string) => {
          if (
            element.substring(0, 5) !== "WZRK_" &&
            element !== "email_CT" &&
            !localStoragePreserveKeys.includes(element)
          ) {
            localStorage.removeItem(element);
          }
        });
        Router.push(clientRoutes.welcome());
      });

  apolloClient = useMemo(
    () =>
      buildApolloClient(
        authToken,
        signOut,
        () => setGotTokenNowFetchPatients(true),
        tokenRefresh
      ),
    [authToken]
  );

  const verifyExternalTokenAndLogin = async () => {
    if (isBrowser()) sessionStorage.setItem("utm_source", source);
    const response = await verifyExternalTokenFromAuthService(token, source);
    let authData = response?.response;
    setExternalAuthData(authData);
    if (authData?.askForConsent) {
      setIsConsentFormVisible(true);
      return;
    }
    if (authData) {
      if (!authData.status && !!!authData.authToken) {
        clearData();
        setIsLoginPopupVisible(true);
      } else {
        if (isBrowser()) {
          localStorage.setItem("mobile_num", authData.mobileNumber);
          localStorage.setItem("currentUser", authData.patientId || "");
        }
        setSSOToken(authData.authToken);
      }
    }
  };

  const trackingCalls = (isNewUser: boolean, userId: string) => {
    gtmTracking({
      category: "Profile",
      action: "Register / Login",
      label: "Register",
    });
    dataLayerTracking({
      event: "UserLoggedIn",
      Type: isNewUser ? "Register" : "Login",
      "User ID": userId,
    });
  };

  const primaryProfile = (res) => {
    const profile = _find(
      res?.data?.getPatientByMobileNumber?.patients || [],
      (profile) => profile?.relation?.toLowerCase() === "me"
    );
    return profile?.id;
  };

  useEffect(() => {
    if (!authToken) return;
    Promise.all([
      apolloClient.query<GetPatientByMobileNumber>({
        query: GET_PATIENT_BY_MOBILE_NUMBER,
        variables: {
          mobileNumber: localStorage.getItem("mobile_num"),
        },
      }),
      apolloClient.query<GetPrimaryPatientsByMobileNumber>({
        query: GET_PRIMARY_PATIENTS_BY_MOBILE_NUMBER,
        variables: {
          mobileNumber: localStorage.getItem("mobile_num"),
        },
      }),
    ])
      .then(([allPatientsRes, primaryPatientsRes]) => {
        const primaryPatients =
          primaryPatientsRes?.data?.getPrimaryPatientsByMobileNumber?.patients;
        if (primaryPatients?.length) {
          setPrimaryPatients(primaryPatients);
        }
        if (!allPatientsRes?.data?.getPatientByMobileNumber?.patients?.length) {
          apolloClient
            .query<GetCurrentPatients>({ query: GET_CURRENT_PATIENTS })
            .then((res) => {
              const userId = res?.data?.getCurrentPatients?.patients?.[0]?.id;
              if (!userId) throw "User Id Not Found";
              dataLayerTracking({
                event: "login_completed",
                login_status: "loggedin",
                mobile: sha1(localStorage.getItem("mobile_num")),
                registered: "no",
              });
              localStorage.setItem("currentUser", userId);
              // registerUserOneApollo(userId);
              setCurrentPatientId(userId);
              setAllCurrentPatients(res?.data?.getCurrentPatients?.patients);
              setCurrentPatient(res?.data?.getCurrentPatients?.patients[0]);
              setSignedin(SignedinState.SIGNEDIN);
              trackingCalls(firebaseUser?.isNewUser, userId);
              _urTracking({
                userId: userId,
                isApolloCustomer: false,
                profileFetchedCount: 1,
              });
              setSignInError(false);
            });
        } else {
          if (isUserLoginCompleted) {
            dataLayerTracking({
              event: "login_completed",
              login_status: "loggedin",
              mobile: sha1(localStorage.getItem("mobile_num")),
              registered: allPatientsRes?.data?.getPatientByMobileNumber
                ?.patients[0]?.uhid?.length
                ? "yes"
                : "no",
            });
          }
          const userId = primaryProfile(allPatientsRes);
          if (localStorage.getItem("currentUser")?.length || userId) {
            const patientIds =
              allPatientsRes?.data?.getPatientByMobileNumber?.patients?.map(
                (patient) => patient.id
              ) || [];
            if (
              userId &&
              !patientIds.includes(localStorage.getItem("currentUser"))
            ) {
              localStorage.setItem("currentUser", userId);
              setCurrentPatientId(userId);
            } else {
              setCurrentPatientId(localStorage.getItem("currentUser"));
            }
          }
          trackingCalls(
            firebaseUser?.isNewUser,
            userId || localStorage.getItem("currentUser")
          );
          const currentPatients =
            allPatientsRes?.data?.getPatientByMobileNumber?.patients;
          setAllCurrentPatients(currentPatients);
          const patient = currentPatients
            ? currentPatients.find(
                (patient) => patient.id === localStorage.getItem("currentUser")
              )
            : null;
          // Colliding different use cases can make currentUser which does not exist in
          // currentPatients and the relation can be null (silent login)
          // hence userId will be undefined because of which patient variable will be undefined
          setCurrentPatient(patient || currentPatients[0]);
          setSignedin(SignedinState.SIGNEDIN);
          if (userId) clevertapUserLoginTracking(patient);
          setSignInError(false);
        }
        setIsLoginPopupVisible(false);
        setIsSigningIn(false);
      })
      .catch((e) => {
        console.error("fire base calls errror", e);
        setSignInError(true);
        clearData();
        localStorage.removeItem("currentUser");
      });
  }, [gotTokenNowFetchPatients]);

  async function getFreshTokenBeforeSixtyMinutes(
    tokenJustGotRefetched: boolean
  ) {
    const resolvedRequest = await fetchNewTokenUsingRefreshToken(
      "refresh_token",
      localStorage.getItem("refreshToken")
    );
    if (resolvedRequest.refresh_token)
      localStorage.setItem("refreshToken", resolvedRequest.refresh_token);
    if (tokenJustGotRefetched) setTokenRefresh(true);
    setAuthToken(resolvedRequest.id_token);
  }

  useEffect(() => {
    const defaultCurrentPatient = (() => {
      if (!allCurrentPatients) return null;
      if (allCurrentPatients.length === 1) return allCurrentPatients[0];
      return allCurrentPatients.find((p) => p.relation === Relation.ME) || null;
    })();

    if (!localStorage.getItem("currentUser")) {
      if (defaultCurrentPatient?.id) {
        const currentUserId = defaultCurrentPatient.id;
        const storageItem = localStorage.getItem(`${currentUserId}`);
        if (!storageItem) {
          const cartItems = localStorage.getItem("cartItems");
          if (cartItems) {
            localStorage.setItem(`${currentUserId}`, cartItems);
            localStorage.removeItem("cartItems");
          }
        } else {
          const cartItems = localStorage.getItem("cartItems");
          if (cartItems) {
            const existingCartItems = JSON.parse(storageItem);
            const cartItemsArry = JSON.parse(cartItems);
            cartItemsArry.forEach((item: MedicineCartItem) => {
              const index = existingCartItems.findIndex(
                (cartItem: MedicineCartItem) => cartItem.id === item.id
              );
              if (index > -1) {
                existingCartItems[index].quantity += item.quantity;
              } else {
                existingCartItems.push(item);
              }
            });
            localStorage.setItem(
              `${currentUserId}`,
              JSON.stringify(existingCartItems)
            );
            localStorage.removeItem("cartItems");
          }
        }
        setCurrentPatientId(currentUserId);
      }
    } else if (defaultCurrentPatient && defaultCurrentPatient.id) {
      const currentUserId = defaultCurrentPatient.id;
      const storageItem = localStorage.getItem(`${currentUserId}`);
      if (!storageItem) {
        const cartItems = localStorage.getItem("cartItems");
        if (cartItems) {
          localStorage.setItem(`${currentUserId}`, cartItems);
          localStorage.removeItem("cartItems");
        }
      }
    }
  }, [allCurrentPatients, currentPatientId, setCurrentPatientId]);

  useEffect(() => {
    if (!ssoToken) return;
    if (
      localStorage.getItem("refreshToken") &&
      !!localStorage.getItem("refreshToken").length &&
      localStorage.getItem("refreshToken") !== "undefined" &&
      localStorage.getItem("refreshToken") !== "null"
    ) {
      getFreshTokenBeforeSixtyMinutes(false);
    } else {
      app
        .auth()
        .signInWithCustomToken(ssoToken)
        .then((userCredential) => {
          const currentUser = userCredential.user;
          setFirebaseUser({
            uid: currentUser.uid,
            isNewUser:
              currentUser.metadata.creationTime ===
              currentUser.metadata.lastSignInTime,
          });
          currentUser.getIdToken().then((token) => {
            setIsUserLoginCompleted(true);
            setAuthToken(token);
            if (currentUser.refreshToken)
              localStorage.setItem("refreshToken", currentUser.refreshToken);
            // fireBaseAuthUpdateCalls();
          });
        });
    }
    setInterval(
      () => getFreshTokenBeforeSixtyMinutes(true),
      TOKEN_REFETCH_DURATION
    );
  }, [ssoToken]);

  useEffect(() => {
    if (isConsentFormVisible) return;
    if (externalAuthData && !externalAuthData?.consent) {
      if (!externalAuthData.status && !!!externalAuthData.authToken) {
        clearData();
        setIsLoginPopupVisible(true);
      } else {
        if (isBrowser()) {
          localStorage.setItem("mobile_num", externalAuthData.mobileNumber);
          localStorage.setItem("currentUser", externalAuthData.patientId || "");
        }
        setSSOToken(externalAuthData.authToken);
      }
    }
  }, [isConsentFormVisible]);

  useEffect(() => {
    const params = new URLSearchParams(window?.location?.search);
    let timer;
    if (
      !!window?.location?.search?.length &&
      (params.get("utm_token") || params.get("refresh_token")) &&
      params.get("utm_mobile_number")
    ) {
      const search = window.location.search;
      const idToken = params.get("utm_token") || "";
      const refreshToken = params.get("refresh_token") || "";

      // Mobile APP Autologin Case
      if (
        params.get("utm_mobile_number") &&
        params.get("utm_mobile_number").trim()?.length
      ) {
        const mobileNumber =
          params.get("utm_mobile_number").trim()[0] === "+"
            ? `${params.get("utm_mobile_number").trim()}`
            : `+${params.get("utm_mobile_number").trim()}`;
        localStorage.setItem("mobile_num", mobileNumber);
        if (refreshToken) {
          localStorage.setItem("refreshToken", refreshToken);
          setSSOToken("true");
        } else if (idToken) {
          // THIS FLOW SHOULD BE DEPRECATED AS SOON AS POSSIBLE
          // WRONG DEFINITION BELOW BUT MAKES THE WEBVIEW WORK IN NATIVE APPS.
          localStorage.setItem("refreshToken", idToken);
          setAuthToken(idToken);
        }
      }
    } else if (source && token) {
      try {
        verifyExternalTokenAndLogin();
      } catch (e) {
        console.error(
          "AuthProvider | error while calling verifyExternalTokenAndLogin: ==> ",
          e
        );
      }
    } else {
      if (
        localStorage.getItem("refreshToken") &&
        !!localStorage.getItem("refreshToken").length &&
        localStorage.getItem("refreshToken") !== "undefined" &&
        localStorage.getItem("refreshToken") !== "null"
      ) {
        setSSOToken("true");
      } else {
        (async () => {
          const resolvedRequest = await fetchTokenFromAuthService();
          const currentMobileNumber = resolvedRequest?.response?.mobileNumber;
          const currentSSOToken = resolvedRequest?.response?.authToken;
          if (currentMobileNumber && currentSSOToken) {
            localStorage.setItem(
              "mobile_num",
              resolvedRequest?.response?.mobileNumber
            );
            setSSOToken(currentSSOToken || "");
          } else {
            setSignedin(SignedinState.SIGNEDOUT);
          }
        })();
      }
    }
    setIsSigningIn(false);
    timer = setTimeout(() => clevertapPushNotification(), 7000);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (signedin === SignedinState.SIGNEDIN) {
      const userSubscriptions: any = JSON.parse(
        localStorage.getItem("userSubscriptions")
      );
      const circleSubs = userSubscriptions?.APOLLO?.filter(
        ({ name, status }: { name: string; status: string }) =>
          name === "CIRCLE PLAN" && status === "active"
      );
      setIsUserCircle(Array.isArray(circleSubs) && circleSubs.length > 0);
    }
  }, [signedin, SignedinState]);

  return (
    // @ts-ignore
    <ApolloProvider client={apolloClient}>
      {
        // @ts-ignore
        <ApolloHooksProvider client={apolloClient}>
          <AuthContext.Provider
            value={{
              currentPatientId,
              setCurrentPatientId,
              getPhrNotifyEvent,
              setGetPhrNotifyEvent,
              signInError,
              isSigningIn,
              hasAuthToken,
              authToken,
              signOut,
              isConsentFormVisible,
              setIsConsentFormVisible,
              isLoginPopupVisible,
              setIsLoginPopupVisible,
              isLoading,
              setIsLoading,
              customLoginId,
              verifyExternalTokenAndLogin,
              setIsSigningIn,
              setCustomLoginId,
              app,
              wait,
              setAuthToken,
              firebaseUser,
              setFirebaseUser,
              setSSOToken,
              allCurrentPatients,
              currentPatient,
              signedin,
              setCurrentPatient,
              setAllCurrentPatients,
              isPartnerLogin,
              setIsPartnerLogin,
              setSignedin,
              isUserCircle,
              externalAuthData,
              setExternalAuthData,
              primaryPatients,
              setPrimaryPatients,
              sectionName: sectionName,
              lob: lob,
            }}
          >
            {props.children}
          </AuthContext.Provider>
        </ApolloHooksProvider>
      }
    </ApolloProvider>
  );
};

export const buildVaccineApolloClient = (
  authToken: string,
  handleUnauthenticated: () => void
) => {
  const errorLink = onError((error) => {
    const { graphQLErrors, operation, forward } = error;
    if (!isProduction) console.error(error);
    if (graphQLErrors) {
      const unauthenticatedError = graphQLErrors.some(
        (gqlError) =>
          gqlError.extensions && gqlError.extensions.code === "UNAUTHENTICATED"
      );
      if (unauthenticatedError) handleUnauthenticated();
    }
  });
};
